import React, { useEffect, useMemo, useState } from "react";
import { withRouter } from "react-router-dom";
import { useTable, useSortBy, usePagination } from "react-table";
import TableHeaders from "./TableHeaders";
import TableBody from "./TableBody";
import TableControls from "./TableControls";
import { shortenTableEntry } from "../utils/formatter";
import { useSelector } from "react-redux";
import { useTableState } from "../hooks/useTableState";

import getAudiobooksByBuisnessType from "../api/getAudiobooksByBuisnessType";
import getAudioBooksLinkId from "../api/getAudioBooksLinkId";
import Button from "./Button";
import { exportAudiobooksToExcel } from "../utils/ExportToExcel";

import OverTableButtons from "../components/OverTableButtons";
import PlainContainer from "../components/PlainContainer";
import TableSearch from "../components/TableSearch";
import LibraryFilter from "./LibraryFilter";
import Spinner from "./Spinner/Spinner";
import { enqueueSnackbar } from "notistack";
import { SEARCH_DEBOUNCE_TIME } from "../utils/constants";
import { debounce } from "../utils/debounce";

import { useQuery, useQueryClient } from 'react-query';

//let users = require("../mock/mock_audiobooks_view.json");

const col = [
  { Header: "#", accessor: "idx", columnType: "col-sm-1" },
  { Header: "ID", accessor: "token_symbol", columnType: "col-sm-1" },
  {
    Header: "ISBN", accessor: "isbn", columnType: "col-sm-1",
    Cell: ({ value }) => {
      return value.replace(/\D/g, '');
    }
  },
  {
    Header: "Tytuł",
    accessor: "name",
    columnType: "col-sm-3",
    Cell: ({ value }) => {
      return shortenTableEntry(value, 45);
    },
  },
  {
    Header: "Autor",
    accessor: "autor",
    columnType: "col-sm-3"
  },
  { Header: "Ilość", accessor: "RazemInOut", columnType: "col-sm-1" },
  {
    Header: "Półka",
    accessor: "Ballance",
    columnType: "col-sm-1",
  },
  // {
  //   Header: "Oczekujący",
  //   accessor: "amountWaiting",
  //   columnType: "col-sm-1",
  // },
  {
    Header: "Wypoż.",
    accessor: "sumaBorrow",
    columnType: "col-sm-1",
  },
];

const AudiobooksTable = ({ history }) => {

  const [chosenLibrary, setChosenLibrary] = useState("none");
  const [filteredData, setFiltered] = useState([]);

  const [tableState, updateTableState] = useTableState('audiobooks');
  const { searchInput, pageIndex, sortBy } = tableState;

  const loggedUser = useSelector(state => state.authentication.user)

  const columns = useMemo(() => col, []);

  const {
    data: audiobooks = [],
    isLoading,
    refetch,
    error
  } = useQuery(
    ['audiobooks', loggedUser.libAddress, loggedUser.username], // Query key
    () => getAudiobooksByBuisnessType(loggedUser.libAddress, loggedUser.username), // Query function
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: 5 * 60 * 1000, // 5 minutes
      enabled: !!loggedUser.libAddress && !!loggedUser.username,
      refetchOnWindowFocus: true,
    }
  );
  const handleRefresh = () => {
    refetch();
  };

  const data = useMemo(() => filteredData, [filteredData]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    pageOptions,
    state,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    prepareRow,
    gotoPage,
    pageCount,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageSize: 18,
        pageIndex: tableState.pageIndex,
        filters: tableState.filters,
        sortBy: tableState.sortBy
      },
      autoResetSortBy: false,
      autoResetPage: false,
      autoResetFilters: false,
    },
    useSortBy,
    usePagination
  );

  // Use a separate useEffect to sync changes to Redux
  useEffect(() => {
    // Only update Redux when the page actually changes (not during init)
    if (state.pageIndex !== tableState.pageIndex) {
      updateTableState({ pageIndex: state.pageIndex });
    }
  }, [state.pageIndex, tableState.pageIndex, updateTableState]); // Watch for changes in table's pageIndex

  useEffect(() => {
    // Update Redux when sorting changes
    if (JSON.stringify(state.sortBy) !== JSON.stringify(tableState.sortBy)) {
      updateTableState({ sortBy: state.sortBy });
    }
  }, [state.sortBy, tableState.sortBy, updateTableState]); // Watch for changes in table's sortBy


  const openAudiobook = (audiobookID) => {
    history.push(`${process.env.PUBLIC_URL}/audiobook/${audiobookID}`);
  };

  const handleChange = (event) => {
    gotoPage(0);
    updateTableState({ searchInput: event.target.value });
  };

  const changeFilter = (event) => { setChosenLibrary(event.target.value) };

  useEffect(() => {
    const filterTransactions = () => {
      if (searchInput.length > 2) {

        let results = audiobooks.filter((value) => {
          if (
            value.name &&
            value.token_symbol &&
            value.autor
          ) {
            // value.name = decodeURIComponent(value.name).replace(/\+/g, " ");
            // value.autor = decodeURIComponent(value.autor).replace(/\+/g, " ");

            return (
              value.token_symbol.includes(searchInput) ||
              value.name.toLowerCase().includes(searchInput.toLowerCase()) ||
              value.autor.toLowerCase().includes(searchInput.toLowerCase()));
          } else {
            return null;
          }
        });
        results = results.map((el, i) => ({ ...el, idx: i + 1 }));
        setFiltered(results);

      } else {
        let audiobookswIds = audiobooks.map((el, i) => ({ ...el, idx: i + 1 }));
        setFiltered(audiobookswIds);

      }
    };
    debounce(filterTransactions, SEARCH_DEBOUNCE_TIME)();
  }, [searchInput, audiobooks]);

  return (
    <div>
      <OverTableButtons
        widthInVw={95}
        heightInVh={4.1}
        customStyles={{ top: "15.5vh" }}
      >
        <Button
          text=
          "Odśwież"
          isDisabled={false}
          styleOptions={{
            float: "right",
            boxShadow: "2px 4px 18px -15px rgba(255,0,0,0.31)",
          }}
          onClick={handleRefresh}
        />
        <TableSearch
          name="searchInputField"
          value={searchInput || ""}
          onChange={handleChange}
        />
        {

          (loggedUser && loggedUser.business_type === "library") ?
            <>
              {(loggedUser.libShop) ?
                <>
                  <Button
                    text="Zakup Audiobooków"
                    styleOptions={{
                      display: "inline",
                      padding: "0.25rem 0.5rem",
                      marginLeft: "1rem",
                      minWidth: "100px"
                    }}
                    isDisabled={false}
                    onClick={async () => await purchaseAudiobooksLink(loggedUser)}
                  />
                </> : null}
              <Button
                text="Eksportuj listę..."
                styleOptions={{
                  display: "inline",
                  padding: "0.25rem 0.5rem",
                  marginLeft: "1rem",
                  minWidth: "100px"
                }}
                isDisabled={
                  // filteredData.length === 0
                  true
                } //{!canNextPage}
              // onClick={async () => await exportAudiobooksToExcel(filteredData, "Audiobooki")}
              />
            </>
            : null
        }
        {
          (loggedUser.business_type === 'audiobook_admin') ?
            <LibraryFilter
              onChange={changeFilter}
            /> :
            null
        }
      </OverTableButtons>
      <PlainContainer
        widthInVw={95}
        heightInVh={'auto'}

        customStyles={{
          top: "19vh",
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          paddingBottom: "0px",
        }}
      >
        <div
          className="container"
          {...getTableProps()}
          style={{ maxWidth: "100%", maxHeight: "100%" }}
        >
          {!isLoading ?
            <>
              <TableHeaders headerGroups={headerGroups} />
              <TableBody
                {...getTableBodyProps()}
                rows={page}
                prepareRow={prepareRow}
                onClick={openAudiobook}
                maxHeight={"67vh"}
              />
              <TableControls
                pageOptions={pageOptions}
                nextPage={nextPage}
                previousPage={previousPage}
                canNextPage={canNextPage}
                canPreviousPage={canPreviousPage}
                pageIndex={pageIndex}
                gotoPage={gotoPage}
                pageCount={pageCount}
              />
            </>
            : <div style={{ textAlign: "-webkit-center" }}>
              <Spinner customStyles={{ fontSize: "30px", margin: "40px" }} />
            </div>
          }
        </div>
      </PlainContainer>
    </div>
  );
};

export default withRouter(AudiobooksTable);

export async function purchaseAudiobooksLink(loggedUser) {
  // open new website 
  try {
    const data = await getAudioBooksLinkId({
      address: loggedUser.libAddress,
      jwtToken: loggedUser.jwtToken,
    });
    if (loggedUser.business_type === 'library' && data) {
      window.open('https://libshop.audiobook.pl?linkId=' + data);
    } else {
      window.open('https://shoppro.audiobook.pl?linkId=' + data);
    }
  } catch (error) {
    if (error.message === "Invalid or missing JWT token") {
      enqueueSnackbar("Sesja wygasła. Wyloguj się i zaloguj ponownie", { variant: "error" });
    } else {
      enqueueSnackbar(`Błąd: ${error.message}`, { variant: "error" });
    }
  }
}

